import { Edge, Node } from "@xyflow/react";
import { DiagramLogData } from "components/CC/atoms/diagramLogAtom";
import { NODE_REGISTRY } from "components/CC/Nodes/node-config";
import { NodeType } from "components/CC/types/enums";

import { ParameterCategory } from "./ParameterPicker";
import {
    createFunctionCategories,
    createParameterCategoriesFromData,
    createPreviousStepsCategory,
    createRowsCategory,
    createSystemParameterCategory,
    CustomFieldData,
    PreviousStep,
    RowsConfig,
} from "./parameterUtils";

export type PickerCategoryType = "form_fields" | "keys" | "system" | "steps" | "rows" | "functions";

interface PreviousStepsConfig {
    nodes: Node[];
    edges: Edge[];
    currentNodeId?: string;
    defaultSetTitle: string;
    httpStepAggregates?: DiagramLogData;
}

interface ResolvedPreviousStepsConfig extends PreviousStepsConfig {
    defaultModifyTitle: string;
    /** Localized label for the synthetic "General" entry — see getPreviousSteps. */
    generalTitle: string;
}

export interface BuildParameterPickerCategoriesOptions {
    t: (key: string) => string;
    allowedCategories: PickerCategoryType[];
    formFields?: App.Data.FormFieldData[];
    customFields?: CustomFieldData[];
    previousSteps?: PreviousStepsConfig;
    rows?: RowsConfig;
}

interface NestedPathTreeNode {
    children: Map<string, NestedPathTreeNode>;
    isTerminal: boolean;
}

const createNestedPathTreeNode = (): NestedPathTreeNode => ({
    children: new Map<string, NestedPathTreeNode>(),
    isTerminal: false,
});

const buildPreviousStepValueTree = (
    rootName: string,
    nodeId: string,
    paths: string[][]
): PreviousStep["values"][number] | null => {
    if (paths.length === 0) return null;

    const seen = new Set<string>();
    const uniquePaths = paths.filter((path) => {
        if (path.length === 0) return false;
        const key = JSON.stringify(path);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    if (uniquePaths.length === 0) return null;

    const root = createNestedPathTreeNode();
    uniquePaths.forEach((path) => {
        let currentNode = root;
        path.forEach((segment, i) => {
            if (!currentNode.children.has(segment)) {
                currentNode.children.set(segment, createNestedPathTreeNode());
            }
            currentNode = currentNode.children.get(segment)!;
            if (i === path.length - 1) {
                currentNode.isTerminal = true;
            }
        });
    });

    const encodeSegment = (s: string): string => s.replace(/\./g, "%2E");

    const toValues = (treeNode: NestedPathTreeNode, currentPath: string[]): PreviousStep["values"] => {
        return Array.from(treeNode.children.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .flatMap(([segment, childNode]) => {
                const nextPath = [...currentPath, segment];
                const leafId = `${rootName}.${nextPath.map(encodeSegment).join(".")}`;

                if (childNode.children.size === 0) {
                    return [{ id: leafId, name: segment }];
                }

                const nestedChildren = toValues(childNode, nextPath);
                // Node is both terminal (a valid value) and has children — emit a selectable
                // leaf entry first, then the branch entry with children.
                if (childNode.isTerminal) {
                    return [
                        { id: leafId, name: segment },
                        { id: `${nodeId}:${leafId}`, name: segment, children: nestedChildren },
                    ];
                }

                return [{ id: `${nodeId}:${leafId}`, name: segment, children: nestedChildren }];
            });
    };

    return {
        id: `${nodeId}:${rootName}`,
        name: rootName,
        children: toValues(root, []),
    };
};

const getAggregate = (stepId: string, httpStepAggregates?: DiagramLogData) =>
    httpStepAggregates?.find((stepLog) => stepLog.stepId === stepId);

const getHttpStepValues = (
    _node: Node,
    stepId: string,
    httpStepAggregates?: DiagramLogData
): PreviousStep["values"] => {
    // parsedBody/query/headers are always offered as whole-object references, even
    // before any run has logged their sub-paths — the nested trees below (populated from real log
    // data) are added alongside as separate, deeper-path entries once available.
    const values: PreviousStep["values"] = [
        { id: "status", name: "status" },
        { id: "parsedBody", name: "parsedBody", insertAsRawContainer: true },
        { id: "query", name: "query", insertAsRawContainer: true },
        { id: "headers", name: "headers", insertAsRawContainer: true },
    ];
    const aggregate = getAggregate(stepId, httpStepAggregates);

    const parsedBodyTree = buildPreviousStepValueTree("parsedBody", stepId, aggregate?.parsedBodyPaths ?? []);
    const headersTree = buildPreviousStepValueTree("headers", stepId, aggregate?.headersPaths ?? []);
    const queryTree = buildPreviousStepValueTree("query", stepId, aggregate?.queryPaths ?? []);

    if (headersTree) values.push(headersTree);
    if (parsedBodyTree) values.push(parsedBodyTree);
    if (queryTree) values.push(queryTree);

    return values;
};

const getWebhookStepValues = (
    _node: Node,
    stepId: string,
    httpStepAggregates?: DiagramLogData
): PreviousStep["values"] => {
    const aggregate = getAggregate(stepId, httpStepAggregates);

    const bodyTree = buildPreviousStepValueTree("body", stepId, aggregate?.parsedBodyPaths ?? []);
    const headersTree = buildPreviousStepValueTree("headers", stepId, aggregate?.headersPaths ?? []);
    const queriesTree = buildPreviousStepValueTree("queries", stepId, aggregate?.queryPaths ?? []);

    const values: PreviousStep["values"] = [];
    if (bodyTree) values.push(bodyTree);
    if (headersTree) values.push(headersTree);
    if (queriesTree) values.push(queriesTree);

    return values;
};

const getSubScenarioStepValues = (
    _node: Node,
    stepId: string,
    httpStepAggregates?: DiagramLogData
): PreviousStep["values"] => {
    const aggregate = getAggregate(stepId, httpStepAggregates);
    if (!aggregate?.parsedBodyPaths.length) return [];

    const outputTree = buildPreviousStepValueTree("output", stepId, aggregate.parsedBodyPaths);
    return outputTree ? [outputTree] : [];
};

const getPreviousSteps = (config: ResolvedPreviousStepsConfig): PreviousStep[] => {
    const { nodes, edges, currentNodeId, defaultSetTitle, defaultModifyTitle, generalTitle, httpStepAggregates } =
        config;
    if (!currentNodeId) return [];

    const incomingByTarget = new Map<string, Edge[]>();
    edges.forEach((edge) => {
        if (!incomingByTarget.has(edge.target)) {
            incomingByTarget.set(edge.target, []);
        }
        incomingByTarget.get(edge.target)?.push(edge);
    });

    // Seeded with currentNodeId so a back-edge from a cycle (e.g. two steps that loop into each
    // other) can never walk back into the current node itself and have it show up as its own
    // "previous step".
    const visited = new Set<string>([currentNodeId]);
    const orderedUpstreamIds: string[] = [];

    const walkUpstream = (nodeId: string) => {
        const incoming = incomingByTarget.get(nodeId) ?? [];
        incoming.forEach((edge) => {
            if (visited.has(edge.source)) return;
            visited.add(edge.source);
            walkUpstream(edge.source);
            orderedUpstreamIds.push(edge.source);
        });
    };

    walkUpstream(currentNodeId);

    const upstreamNodes = orderedUpstreamIds
        .map((nodeId) => nodes.find((node) => node.id === nodeId))
        .filter((node): node is Node => Boolean(node));

    const hasHttpStep = upstreamNodes.some((node) => node.type === NodeType.Http);

    const steps: PreviousStep[] = [];
    let stepIndex = 1;

    // Only offered when an HTTP step actually precedes this node — status/parsedBody/query/headers
    // only make sense as an HTTP response shape. Unlike a real step, these insert unprefixed (no
    // "${step.id}." scope) since they're not tied to a specific numbered step — see
    // PreviousStep.isGeneral and its handling in createPreviousStepsCategory.
    if (hasHttpStep) {
        steps.push({
            id: "general",
            number: 0,
            title: generalTitle,
            isGeneral: true,
            values: [
                { id: "status", name: "status" },
                { id: "parsedBody", name: "parsedBody", insertAsRawContainer: true },
                { id: "query", name: "query", insertAsRawContainer: true },
                { id: "headers", name: "headers", insertAsRawContainer: true },
            ],
        });
    }

    const resolveStepId = (node: Node): string =>
        ((node.data as Record<string, unknown> | undefined)?.beId as string | undefined) ?? node.id;

    upstreamNodes.forEach((node) => {
        if (node.type === NodeType.Set) {
            const formData = (node.data?.formData ?? {}) as { title?: string; fields?: Array<{ key?: string }> };
            const stepTitle = formData.title?.trim() || defaultSetTitle;
            const uniqueKeys = Array.from(
                new Set(
                    (formData.fields ?? [])
                        .map((field) => field.key?.trim())
                        .filter((key): key is string => Boolean(key))
                )
            );
            const values = uniqueKeys.map((key) => {
                const stripped = key.replace(/^\{(.+)\}$/, "$1");
                return { id: stripped, name: stripped };
            });
            if (values.length > 0) {
                steps.push({
                    id: resolveStepId(node),
                    number: stepIndex++,
                    title: stepTitle,
                    values,
                    color: NODE_REGISTRY[NodeType.Set].color,
                    nodeType: NodeType.Set,
                });
            }
        } else if (node.type === NodeType.Modify) {
            const formData = (node.data?.formData ?? {}) as { title?: string; data?: Array<{ fieldName?: string }> };
            const stepTitle = formData.title?.trim() || defaultModifyTitle;
            const uniqueFieldNames = Array.from(
                new Set(
                    (formData.data ?? [])
                        .map((entry) => entry.fieldName?.trim())
                        .filter((name): name is string => Boolean(name))
                )
            );
            const values = uniqueFieldNames.map((name) => {
                const stripped = name.replace(/^\{(.+)\}$/, "$1");
                return { id: stripped, name: stripped };
            });
            if (values.length > 0) {
                steps.push({
                    id: resolveStepId(node),
                    number: stepIndex++,
                    title: stepTitle,
                    values,
                    color: NODE_REGISTRY[NodeType.Modify].color,
                    nodeType: NodeType.Modify,
                });
            }
        } else if (node.type === NodeType.Http) {
            const formData = (node.data?.formData ?? {}) as { title?: string };
            const stepTitle = formData.title?.trim() || "HTTP";
            const stepId = resolveStepId(node);
            steps.push({
                id: stepId,
                number: stepIndex++,
                title: stepTitle,
                values: getHttpStepValues(node, stepId, httpStepAggregates),
                color: NODE_REGISTRY[NodeType.Http].color,
                nodeType: NodeType.Http,
            });
        } else if (node.type === NodeType.Webhook) {
            const formData = (node.data?.formData ?? {}) as { title?: string };
            const stepTitle = formData.title?.trim() || "Webhook";
            const stepId = resolveStepId(node);
            const values = getWebhookStepValues(node, stepId, httpStepAggregates);
            if (values.length > 0) {
                steps.push({
                    id: stepId,
                    number: stepIndex++,
                    title: stepTitle,
                    values,
                    color: NODE_REGISTRY[NodeType.Webhook].color,
                    nodeType: NodeType.Webhook,
                });
            }
        } else if (node.type === NodeType.SubScenario) {
            const formData = (node.data?.formData ?? {}) as { title?: string };
            const stepTitle = formData.title?.trim() || "Sub-scenario";
            const stepId = resolveStepId(node);
            const values = getSubScenarioStepValues(node, stepId, httpStepAggregates);
            if (values.length > 0) {
                steps.push({
                    id: stepId,
                    number: stepIndex++,
                    title: stepTitle,
                    values,
                    color: NODE_REGISTRY[NodeType.SubScenario].color,
                    nodeType: NodeType.SubScenario,
                });
            }
        }
    });

    return steps;
};

/**
 * Inserts a category into an already-built, ordered category list at the position it would
 * have occupied had it been included in the original `buildParameterPickerCategories` call —
 * i.e. right before the first present category that comes after it in `allowedCategories`.
 *
 * Lets callers compute an expensive, row-independent base once (e.g. "rows" excluded) and then
 * cheaply merge in a per-row category (like "rows") without re-running the whole pipeline.
 */
export const insertCategoryInOrder = (
    base: ParameterCategory[],
    category: ParameterCategory | null,
    categoryId: PickerCategoryType,
    allowedCategories: PickerCategoryType[]
): ParameterCategory[] => {
    if (!category) return base;
    const orderIndex = allowedCategories.indexOf(categoryId);
    // Categories like the "functions" subsections (format_convert, math_functions, ...) don't
    // have their own entry in allowedCategories — only "functions" does — and are always emitted
    // last by buildParameterPickerCategories, so an unmatched id never counts as "before".
    const insertAt = base.filter((c) => {
        const idx = allowedCategories.indexOf(c.id as PickerCategoryType);
        return idx !== -1 && idx < orderIndex;
    }).length;
    const result = [...base];
    result.splice(insertAt, 0, category);
    return result;
};

export const buildParameterPickerCategories = ({
    t,
    allowedCategories,
    formFields,
    customFields,
    previousSteps,
    rows,
}: BuildParameterPickerCategoriesOptions): ParameterCategory[] => {
    const byId = new Map<string, ParameterCategory>();

    if (allowedCategories.includes("form_fields") || allowedCategories.includes("keys")) {
        const base = createParameterCategoriesFromData({ formFields, customFields }, t);
        base.filter((cat) => allowedCategories.includes(cat.id as PickerCategoryType)).forEach((category) =>
            byId.set(category.id, category)
        );
    }

    if (allowedCategories.includes("system")) {
        byId.set("system", createSystemParameterCategory(t));
    }

    if (allowedCategories.includes("steps") && previousSteps) {
        const steps = getPreviousSteps({
            ...previousSteps,
            defaultModifyTitle: t("nodes.modify.title"),
            generalTitle: t("parameter_picker.categories.general"),
        });
        if (steps.length > 0) {
            byId.set("steps", createPreviousStepsCategory(steps, t));
        }
    }

    if (allowedCategories.includes("rows") && rows) {
        const rowsCategory = createRowsCategory(rows, t);
        if (rowsCategory) {
            byId.set("rows", rowsCategory);
        }
    }

    const dataCategories = allowedCategories
        .map((categoryType) => byId.get(categoryType))
        .filter((category): category is ParameterCategory => {
            return category !== undefined && category.items.length > 0;
        });

    const functionCategories = allowedCategories.includes("functions") ? createFunctionCategories(t) : [];

    return [...dataCategories, ...functionCategories];
};
